import { useState, useRef } from "react";
import { sanitizeHTML, validateFile, validateURL, validateAndSanitizeInput } from "@/lib/security";
import { 
  Bold, 
  Italic, 
  Underline, 
  Heading1, 
  Heading2, 
  Heading3,
  Image, 
  Video, 
  Save, 
  ArrowLeft,
  Upload,
  Link2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Eye,
  Crown,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const ContentEditor = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatText = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertHeading = (level: number) => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const headingElement = document.createElement(`h${level}`);
      headingElement.style.fontSize = level === 1 ? '1.875rem' : level === 2 ? '1.5rem' : '1.25rem';
      headingElement.style.fontWeight = 'bold';
      headingElement.style.margin = '1rem 0';
      
      if (range.collapsed) {
        headingElement.textContent = `Überschrift ${level}`;
      } else {
        headingElement.appendChild(range.extractContents());
      }
      
      range.insertNode(headingElement);
      range.selectNodeContents(headingElement);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const insertImage = (src: string, alt: string = "Bild") => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      const container = document.createElement('div');
      container.style.margin = '1rem 0';
      container.style.textAlign = 'center';
      container.contentEditable = 'false';
      container.className = 'image-container';
      container.style.position = 'relative';
      container.style.display = 'inline-block';
      container.style.width = '100%';
      
      const img = document.createElement('img');
      img.src = src;
      img.alt = alt;
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.borderRadius = '8px';
      img.style.border = '1px solid #e2e8f0';
      img.style.cursor = 'move';
      
      const caption = document.createElement('p');
      caption.style.fontSize = '0.875rem';
      caption.style.color = '#6b7280';
      caption.style.marginTop = '0.5rem';
      caption.contentEditable = 'true';
      caption.textContent = alt;
      
      container.appendChild(img);
      container.appendChild(caption);
      
      // Drag and drop functionality
      container.draggable = true;
      container.addEventListener('dragstart', (e) => {
        e.dataTransfer?.setData('text/html', container.outerHTML);
      });
      
      range.insertNode(container);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const insertVideo = (src: string, type: 'youtube' | 'vimeo' | 'file' = 'youtube') => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      const container = document.createElement('div');
      container.style.margin = '1rem 0';
      container.contentEditable = 'false';
      container.className = 'video-container';
      container.style.position = 'relative';
      container.draggable = true;
      
      let videoElement;
      
      if (type === 'youtube') {
        const videoId = extractYouTubeId(src);
        if (videoId) {
          videoElement = document.createElement('iframe');
          videoElement.src = `https://www.youtube.com/embed/${videoId}`;
          videoElement.style.width = '100%';
          videoElement.style.height = '400px';
          videoElement.style.border = 'none';
          videoElement.style.borderRadius = '8px';
        }
      } else if (type === 'vimeo') {
        const videoId = extractVimeoId(src);
        if (videoId) {
          videoElement = document.createElement('iframe');
          videoElement.src = `https://player.vimeo.com/video/${videoId}`;
          videoElement.style.width = '100%';
          videoElement.style.height = '400px';
          videoElement.style.border = 'none';
          videoElement.style.borderRadius = '8px';
        }
      } else {
        videoElement = document.createElement('video');
        videoElement.src = src;
        videoElement.controls = true;
        videoElement.style.width = '100%';
        videoElement.style.maxHeight = '400px';
        videoElement.style.borderRadius = '8px';
      }
      
      if (videoElement) {
        container.appendChild(videoElement);
        
        // Drag and drop functionality
        container.addEventListener('dragstart', (e) => {
          e.dataTransfer?.setData('text/html', container.outerHTML);
        });
        
        range.insertNode(container);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const extractYouTubeId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const extractVimeoId = (url: string) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file security
    const validation = validateFile(file);
    if (!validation.isValid) {
      toast.error(`Datei-Upload fehlgeschlagen: ${validation.error}`);
      return;
    }

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        insertImage(result, validateAndSanitizeInput(file.name, 255));
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        insertVideo(result, 'file');
      };
      reader.readAsDataURL(file);
    }
    toast.success(`${file.type.startsWith('image/') ? 'Bild' : 'Video'} wurde sicher eingefügt`);
  };

  const handleInsertImageUrl = () => {
    const url = prompt("Bild-URL eingeben:");
    if (!url) return;

    const validation = validateURL(url);
    if (!validation.isValid) {
      toast.error(`URL ungültig: ${validation.error}`);
      return;
    }

    insertImage(url);
    toast.success("Bild wurde sicher eingefügt");
  };

  const handleInsertVideoUrl = () => {
    const url = prompt("Video-URL eingeben (YouTube, Vimeo oder direkter Link):");
    if (!url) return;

    const validation = validateURL(url);
    if (!validation.isValid) {
      toast.error(`URL ungültig: ${validation.error}`);
      return;
    }

    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      insertVideo(url, 'youtube');
    } else if (url.includes('vimeo.com')) {
      insertVideo(url, 'vimeo');
    } else {
      insertVideo(url, 'file');
    }
    toast.success("Video wurde sicher eingefügt");
  };

  const handleSave = () => {
    const contentHtml = editorRef.current?.innerHTML || "";
    console.log("Saving content:", { 
      title, 
      description, 
      content: contentHtml, 
      tags: tags.split(",").map(tag => tag.trim()),
      isPremium 
    });
    toast.success("Inhalt wurde gespeichert!");
  };

  const getContentForPreview = () => {
    const content = editorRef.current?.innerHTML || "";
    return sanitizeHTML(content);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {title || "Neuer Inhalt"}
              </h1>
              <p className="text-sm text-muted-foreground">
                Erstellen und bearbeiten Sie Archiv-Inhalte
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPreview(!isPreview)}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              {isPreview ? "Bearbeiten" : "Vorschau"}
            </Button>
            <Button
              onClick={handleSave}
              variant="vodafone"
              size="sm"
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              Speichern
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {!isPreview ? (
              // Edit Mode
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Grundinformationen</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="title">Titel</Label>
                      <Input
                        id="title"
                        placeholder="Geben Sie den Titel ein..."
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="description">Beschreibung</Label>
                      <Input
                        id="description"
                        placeholder="Kurze Beschreibung des Inhalts..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="tags">Tags (kommagetrennt)</Label>
                      <Input
                        id="tags"
                        placeholder="z.B. 5G, Netzwerk, Implementation"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Inhalt Editor</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Rich Text Editor Toolbar */}
                    <div className="border rounded-t-md bg-muted/50 p-2 flex flex-wrap gap-1 mb-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => formatText('bold')}
                      >
                        <Bold className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => formatText('italic')}
                      >
                        <Italic className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => formatText('underline')}
                      >
                        <Underline className="h-4 w-4" />
                      </Button>
                      <Separator orientation="vertical" className="h-6 my-1" />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => insertHeading(1)}
                      >
                        <Heading1 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => insertHeading(2)}
                      >
                        <Heading2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => insertHeading(3)}
                      >
                        <Heading3 className="h-4 w-4" />
                      </Button>
                      <Separator orientation="vertical" className="h-6 my-1" />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => formatText('justifyLeft')}
                      >
                        <AlignLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => formatText('justifyCenter')}
                      >
                        <AlignCenter className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={() => formatText('justifyRight')}
                      >
                        <AlignRight className="h-4 w-4" />
                      </Button>
                      <Separator orientation="vertical" className="h-6 my-1" />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={handleInsertImageUrl}
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Bild URL
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Datei
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={handleInsertVideoUrl}
                      >
                        <Video className="h-4 w-4 mr-1" />
                        Video
                      </Button>
                    </div>
                    
                    <div
                      ref={editorRef}
                      contentEditable
                      className="min-h-96 p-4 border border-t-0 rounded-b-md outline-none prose prose-lg max-w-none bg-background"
                      style={{
                        lineHeight: '1.6',
                        fontSize: '16px'
                      }}
                      suppressContentEditableWarning={true}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const htmlData = e.dataTransfer.getData('text/html');
                        if (htmlData) {
                          document.execCommand('insertHTML', false, htmlData);
                        }
                      }}
                    >
                      <p>Beginnen Sie hier mit der Eingabe Ihres Inhalts...</p>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-2">
                      Rich-Text-Editor mit flexibler Positionierung. Bilder und Videos können per Drag & Drop neu positioniert werden.
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : (
              // Preview Mode
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl flex items-center gap-2">
                        {title || "Unbenannter Inhalt"}
                        {isPremium && <Crown className="h-5 w-5 text-primary" />}
                      </CardTitle>
                      <p className="text-muted-foreground mt-2">
                        {description || "Keine Beschreibung verfügbar"}
                      </p>
                    </div>
                    {isPremium && (
                      <Badge className="bg-gradient-primary text-white">
                        Premium
                      </Badge>
                    )}
                  </div>
                  
                  {tags && (
                    <div className="flex flex-wrap gap-1 mt-4">
                      {tags.split(",").map((tag, index) => (
                        <Badge key={index} variant="secondary">
                          {tag.trim()}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardHeader>
                
                <CardContent>
                  <div 
                    className="prose prose-gray max-w-none"
                    dangerouslySetInnerHTML={{ __html: getContentForPreview() }}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar Settings */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Einstellungen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="premium-toggle" className="font-medium">
                      Premium Inhalt
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Nur für zahlende Mitglieder
                    </p>
                  </div>
                  <Switch
                    id="premium-toggle"
                    checked={isPremium}
                    onCheckedChange={setIsPremium}
                  />
                </div>
                
                <Separator />
                
                <div>
                  <Label className="font-medium">Status</Label>
                  <Badge variant="outline" className="ml-2">
                    Entwurf
                  </Badge>
                </div>
                
                <div>
                  <Label className="font-medium">Autor</Label>
                  <p className="text-sm text-muted-foreground">
                    Aktueller Benutzer
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Hilfe & Tipps</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>
                  <strong>Editor Funktionen:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Drag & Drop für Bilder/Videos</li>
                  <li>YouTube/Vimeo URL einfügen</li>
                  <li>Datei-Upload für Medien</li>
                  <li>Rich-Text Formatierung</li>
                  <li>Flexible Positionierung</li>
                </ul>
                
                <Separator className="my-3" />
                
                <p>
                  <strong>Premium Inhalte:</strong>
                </p>
                <p className="text-xs">
                  Aktivieren Sie "Premium Inhalt" für vertrauliche oder 
                  erweiterte Informationen, die nur zahlenden Mitgliedern 
                  zur Verfügung stehen sollen.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  );
};

export default ContentEditor;